import React, { useState } from "react";
import { X, Loader2 } from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { crm as crmApi } from "@/mock";
import { PhoneField } from "@/components/PhoneField";

const INDUSTRIES = [
  ["general", "General Business"],
  ["education", "Education"],
  ["healthcare", "Healthcare"],
  ["professional_services", "Professional Services"],
  ["financial_services", "Financial Services"],
  ["technology", "Technology"],
  ["manufacturing", "Manufacturing"],
  ["retail", "Retail"],
  ["hospitality", "Hospitality"],
  ["nonprofit", "Nonprofit"],
];

const SIZES = ["1-50", "51-200", "201-500", "501-1000", "1000+"];

/**
 * Admin-only "Add Lead" modal — manual lead entry form.
 * Creates a prospect with source="Manual" so it's distinguishable from
 * organic website submissions. Mandatory fields: company, contact name,
 * email, industry. Optional: phone, website, size, role, pain note.
 */
export default function AddLeadModal({ open, onClose, onCreated }) {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: "",
    full_name: "",
    email: "",
    phone: "",
    website: "",
    role: "",
    industry: "",
    employee_count: "",
    pain_note: "",
  });

  if (!open) return null;

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.company_name || !form.full_name || !form.email || !form.industry) {
      toast.error("Company, contact name, email, and industry are required.");
      return;
    }
    setSaving(true);
    try {
      const prospect = await crmApi.createLead(
        {
          vertical: form.industry,
          company: form.company_name,
          full_name: form.full_name,
          email: form.email,
          phone: form.phone,
          website: form.website,
          role: form.role,
          company_size: form.employee_count,
          pain_areas: [],
          notes: form.pain_note,
          source: "Manual",
        },
        null,
      );
      toast.success("Lead created");
      onCreated && onCreated(prospect);
      onClose();
    } catch (err) {
      toast.error(err?.message || "Failed to create lead");
    } finally {
      setSaving(false);
    }
  };

  const labelCls = "block text-[11.5px] font-semibold tracking-[0.10em] uppercase mb-1.5";
  const inputCls = "w-full h-10 rounded-lg border bg-white px-3 text-[13px] outline-none focus:border-[#3B4FE4]";

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ background: "rgba(15,17,23,0.55)" }}
      data-testid="add-lead-modal"
    >
      <div
        className="w-full max-w-[600px] rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ border: "1px solid rgba(0,0,0,0.08)" }}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
          <div>
            <div className="text-[11.5px] font-bold tracking-[0.14em] uppercase" style={{ color: "#9499A8" }}>Add</div>
            <h3 className="text-[20px] font-bold" style={{ color: "#0F1117" }}>New Lead</h3>
          </div>
          <button type="button" onClick={onClose} className="h-8 w-8 inline-flex items-center justify-center rounded-md hover:bg-[#FAFAF8]" data-testid="add-lead-close">
            <X className="h-4 w-4" style={{ color: "#5C6070" }} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls} style={{ color: "#5C6070" }}>Company Name *</label>
              <input type="text" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} placeholder="e.g., Summit Grove Capital" data-testid="add-lead-company" />
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Contact Name *</label>
              <input type="text" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} data-testid="add-lead-name" />
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Role / Title</label>
              <input type="text" value={form.role} onChange={(e) => set("role", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} placeholder="COO, CEO…" data-testid="add-lead-role" />
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Email *</label>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} data-testid="add-lead-email" />
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Phone</label>
              <PhoneField value={form.phone} onChange={(v) => set("phone", v)} testid="add-lead-phone" />
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Website</label>
              <input type="text" value={form.website} onChange={(e) => set("website", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} placeholder="company.com" data-testid="add-lead-website" />
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Industry *</label>
              <select value={form.industry} onChange={(e) => set("industry", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} data-testid="add-lead-industry">
                <option value="">Select industry…</option>
                {INDUSTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls} style={{ color: "#5C6070" }}>Company Size</label>
              <select value={form.employee_count} onChange={(e) => set("employee_count", e.target.value)} className={inputCls} style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} data-testid="add-lead-size">
                <option value="">Select size…</option>
                {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls} style={{ color: "#5C6070" }}>Pain Point / Notes</label>
              <textarea value={form.pain_note} onChange={(e) => set("pain_note", e.target.value)} rows={3} className="w-full rounded-lg border bg-white px-3 py-2 text-[13px] outline-none focus:border-[#3B4FE4] resize-none" style={{ borderColor: "rgba(0,0,0,0.10)", color: "#0F1117" }} placeholder="What's driving the conversation?" data-testid="add-lead-notes" />
            </div>
          </div>
          <div className="flex items-center justify-end gap-2 mt-6 pt-4 border-t" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
            <button type="button" onClick={onClose} className="px-4 h-10 rounded-lg text-[13px] font-semibold hover:bg-[#FAFAF8]" style={{ color: "#5C6070" }} data-testid="add-lead-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="px-5 h-10 inline-flex items-center gap-2 rounded-lg text-[13px] font-semibold text-white" style={{ background: "#C79A3B" }} data-testid="add-lead-submit">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Create Lead
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
